import { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { api_url } from "../../../config/config";

const formatDate = (d) =>
  new Date(d).toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });

const readTime = (content) => Math.max(1, Math.ceil((content || "").length / 800));

/* ── Skeleton card ── */
const SkeletonCard = () => (
  <div className="bg-white border border-[#e2eaea] rounded-2xl overflow-hidden animate-pulse flex-shrink-0 w-[280px] sm:w-[300px] lg:w-[320px]">
    <div className="h-[220px] bg-[#e2eaea]" />
    <div className="p-4 flex flex-col gap-2.5">
      <div className="h-2.5 w-16 bg-[#e2eaea] rounded-full" />
      <div className="h-4 w-3/4 bg-[#e2eaea] rounded-full" />
      <div className="h-3 w-full bg-[#e2eaea] rounded-full" />
      <div className="h-3 w-2/3 bg-[#e2eaea] rounded-full" />
    </div>
  </div>
);

const THEMES = [
  {
    cardBg: "bg-gradient-to-b from-[#eaf4f3] to-white",
    border: "border-[#c5d5d4] hover:border-[#5fbdb3]",
    accent: "from-[#1e4a46] via-[#5fbdb3] to-[#1e4a46]",
    badge: "bg-[#eaf4f3] text-[#1e4a46] border-[#d4ecea]",
    shadow:
      "shadow-[0_2px_12px_rgba(95,189,179,0.10)] hover:shadow-[0_10px_32px_rgba(95,189,179,0.22)]",
  },
  {
    cardBg: "bg-gradient-to-b from-[#fff3ee] to-white",
    border: "border-[#ffd5c2] hover:border-[#ff601e]",
    accent: "from-[#ff601e] via-[#ffd5c2] to-[#ff601e]",
    badge: "bg-[#fff3ee] text-[#cc4c18] border-[#ffd5c2]",
    shadow:
      "shadow-[0_2px_12px_rgba(255,96,30,0.09)] hover:shadow-[0_10px_32px_rgba(255,96,30,0.22)]",
  },
  {
    cardBg: "bg-gradient-to-b from-[#f4fbfa] to-white",
    border: "border-[#d4ecea] hover:border-[#3a7a74]",
    accent: "from-[#3a7a74] via-[#5fbdb3] to-[#3a7a74]",
    badge: "bg-[#eaf4f3] text-[#284b49] border-[#c5d5d4]",
    shadow:
      "shadow-[0_2px_12px_rgba(58,122,116,0.09)] hover:shadow-[0_10px_32px_rgba(58,122,116,0.20)]",
  },
  {
    cardBg: "bg-gradient-to-b from-[#fff8f0] to-white",
    border: "border-[#ffd5c2] hover:border-[#ff601e]/70",
    accent: "from-[#ff601e] to-[#e55519]",
    badge: "bg-[#fff3ee] text-[#c44a14] border-[#ffd5c2]",
    shadow:
      "shadow-[0_2px_12px_rgba(255,96,30,0.08)] hover:shadow-[0_10px_32px_rgba(255,96,30,0.20)]",
  },
];

const ArticleCarousel = () => {
  const [blogPosts, setBlogPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const scrollRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchBlogs = async () => {
      try {
        const response = await axios.get(`${api_url}/blog`);
        const blogs = Array.isArray(response.data?.data)
          ? response.data.data
          : Array.isArray(response.data?.blogs)
            ? response.data.blogs
            : [];
        setBlogPosts(blogs.filter((b) => b.status === "Published"));
      } catch (error) {
        console.error("Failed to fetch blog posts:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchBlogs();
  }, []);

  const handleNavigation = (slug) => {
    try {
      sessionStorage.setItem("scrollToSection", "article-section");
    } catch (_) {}
    navigate(`/blogs/${slug}`);
  };

  /* Card width 320px + gap 16px = 336px × 3 cards per scroll */
  const scrollLeft = () =>
    scrollRef.current?.scrollBy({ left: -336 * 3, behavior: "smooth" });
  const scrollRight = () =>
    scrollRef.current?.scrollBy({ left: 336 * 3, behavior: "smooth" });

  return (
    <section
      className="w-full font-poppins py-6 px-4 sm:px-8 lg:px-6"
      id="article-section"
    >
      {/* ── Section header ── */}
      <div className="flex items-end justify-between mb-6 sm:mb-8">
        <div>
          <div className="flex items-center gap-2 mb-1.5">
            <span className="w-5 h-[2px] bg-[#ff601e] rounded-full" />
            <span className="text-[10px] sm:text-[11px] font-bold uppercase tracking-widest text-[#ff601e]">
              Wellness Articles
            </span>
          </div>
          <h2 className="font-bold text-[20px] sm:text-[24px] lg:text-[28px] text-[#1e4a46] leading-tight">
            Health &amp; Wellness Blog
          </h2>
          <p className="text-[13px] text-[#8faba9] mt-1 hidden sm:block">
            Expert tips, guides and health insights from our pharmacists
          </p>
        </div>

        <Link
          to="/blogs"
          onClick={() => {
            try {
              sessionStorage.setItem("scrollToSection", "article-section");
            } catch (_) {}
          }}
        >
          <button className="flex items-center gap-1.5 bg-[#162c2a] hover:bg-[#ff601e] text-white font-semibold text-[11px] sm:text-[12px] px-4 h-8 rounded-xl transition-all duration-200 shadow-[0_2px_8px_rgba(22,44,42,0.25)] hover:shadow-[0_4px_14px_rgba(255,96,30,0.4)] hover:-translate-y-0.5 whitespace-nowrap border-none cursor-pointer">
            View All
            <svg
              width="11"
              height="11"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M5 12h14M12 5l7 7-7 7" />
            </svg>
          </button>
        </Link>
      </div>

      {/* ── Carousel ── */}
      <div className="relative">
        {/* Left arrow */}
        <button
          onClick={scrollLeft}
          className="absolute -left-3 sm:-left-5 top-1/2 -translate-y-1/2 z-20 w-8 h-8 sm:w-10 sm:h-10 rounded-xl bg-white border-[1.5px] border-[#e2eaea] shadow-[0_2px_10px_rgba(22,44,42,0.10)] flex items-center justify-center text-[#374747] hover:bg-[#eaf4f3] hover:border-[#5fbdb3] hover:text-[#1e4a46] transition-all duration-200 cursor-pointer"
        >
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M19 12H5M12 5l-7 7 7 7" />
          </svg>
        </button>

        {/* Scroll container */}
        <div
          ref={scrollRef}
          className="flex gap-4 overflow-x-auto scrollbar-hide scroll-smooth py-2 px-1"
          style={{ scrollSnapType: "x mandatory" }}
        >
          {loading ? (
            Array.from({ length: 6 }).map((_, i) => <SkeletonCard key={i} />)
          ) : blogPosts.length === 0 ? (
            <div className="flex-1 flex flex-col items-center justify-center py-16 gap-3">
              <div className="w-14 h-14 rounded-2xl bg-[#eaf4f3] border border-[#d4ecea] flex items-center justify-center">
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#5fbdb3"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                  <polyline points="14 2 14 8 20 8" />
                </svg>
              </div>
              <p className="text-[14px] font-semibold text-[#374747]">
                No articles yet
              </p>
              <p className="text-[12px] text-[#8faba9]">
                Check back soon for health tips.
              </p>
            </div>
          ) : (
            blogPosts.map((article, index) => {
              const t = THEMES[index % THEMES.length];
              return (
                <div
                  key={article.blog_id || article.slug}
                  onClick={() => handleNavigation(article.slug)}
                  /* ── CHANGED: width 280→320px, minWidth matches ── */
                  style={{
                    scrollSnapAlign: "start",
                    minWidth: "320px",
                    maxWidth: "320px",
                  }}
                  className={`group flex-shrink-0
                    border-[1.5px] ${t.border} ${t.cardBg} ${t.shadow}
                    rounded-2xl overflow-hidden cursor-pointer
                    hover:-translate-y-2 transition-all duration-300 flex flex-col relative`}
                >
                  {/* Top accent bar */}
                  <div
                    className={`absolute top-0 left-0 right-0 h-[3px] bg-gradient-to-r ${t.accent} opacity-0 group-hover:opacity-100 transition-opacity duration-300 z-10`}
                  />

                  {/* ── CHANGED: image height 170px → 220px ── */}
                  <div
                    className="relative flex-shrink-0 overflow-hidden"
                    style={{ height: "220px" }}
                  >
                    <img
                      src={article.blog_images?.[0]?.image_url}
                      alt={article.title || "Blog"}
                      className="w-full h-full object-cover object-center block group-hover:scale-105 transition-transform duration-500"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = "/default.svg";
                      }}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#0a1715]/50 via-transparent to-transparent" />

                    {/* Blog type badge */}
                    {article.blog_type?.name && (
                      <span
                        className={`absolute top-2.5 left-2.5 text-[9px] font-bold px-2 py-0.5 rounded-full border ${t.badge}`}
                      >
                        {article.blog_type.name}
                      </span>
                    )}

                    {/* Date badge */}
                    <div className="absolute bottom-2.5 right-2.5 flex items-center gap-1 bg-[#0a1715]/65 backdrop-blur-sm px-2 py-0.5 rounded-lg">
                      <svg
                        width="8"
                        height="8"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="#5fbdb3"
                        strokeWidth="2.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <rect x="3" y="4" width="18" height="18" rx="2" />
                        <line x1="16" y1="2" x2="16" y2="6" />
                        <line x1="8" y1="2" x2="8" y2="6" />
                        <line x1="3" y1="10" x2="21" y2="10" />
                      </svg>
                      <span className="text-[9px] font-medium text-white/80">
                        {formatDate(article.published_at)}
                      </span>
                    </div>
                  </div>

                  {/* ── Content (unchanged) ── */}
                  <div className="p-3.5 flex flex-col gap-2 flex-1">
                    <div className="flex items-center gap-1.5">
                      <div className="w-5 h-5 rounded-full bg-[#1e4a46] flex items-center justify-center flex-shrink-0">
                        <svg
                          width="9"
                          height="9"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="white"
                          strokeWidth="2.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                          <circle cx="12" cy="7" r="4" />
                        </svg>
                      </div>
                      <p className="text-[11px] font-semibold text-[#5fbdb3] truncate flex-1">
                        {article.author ||
                          article.created_admin?.name ||
                          "Health Team"}
                      </p>
                      <span className="text-[#e2eaea] text-[9px]">·</span>
                      <p className="text-[10px] text-[#8faba9] whitespace-nowrap flex-shrink-0">
                        {readTime(article.content)} min
                      </p>
                    </div>

                    <h3 className="font-bold text-[13px] text-[#1e4a46] leading-snug group-hover:text-[#ff601e] transition-colors duration-200 line-clamp-2 flex-1">
                      {article.title || "Untitled"}
                    </h3>

                    {article.subtitle && (
                      <p className="text-[13px] text-[#607d7b] line-clamp-2 leading-relaxed">
                        {article.subtitle}
                      </p>
                    )}

                    <div className="w-full h-px bg-black/[0.05] mt-auto" />

                    <div className="flex items-center justify-between pt-1">
                      <span className="flex items-center gap-0.5 text-[10px] font-bold text-[#1e4a46] group-hover:text-[#ff601e] transition-colors">
                        Read more
                        <svg
                          width="9"
                          height="9"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <path d="M5 12h14M12 5l7 7-7 7" />
                        </svg>
                      </span>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Right arrow */}
        <button
          onClick={scrollRight}
          className="absolute -right-3 sm:-right-5 top-1/2 -translate-y-1/2 z-20 w-8 h-8 sm:w-10 sm:h-10 rounded-xl bg-white border-[1.5px] border-[#e2eaea] shadow-[0_2px_10px_rgba(22,44,42,0.10)] flex items-center justify-center text-[#374747] hover:bg-[#eaf4f3] hover:border-[#5fbdb3] hover:text-[#1e4a46] transition-all duration-200 cursor-pointer"
        >
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M5 12h14M12 5l7 7-7 7" />
          </svg>
        </button>
      </div>

      <style>{`
        .scrollbar-hide::-webkit-scrollbar { display:none; }
        .scrollbar-hide { -ms-overflow-style:none; scrollbar-width:none; }
      `}</style>
    </section>
  );
};

export default ArticleCarousel;
